import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { residentKeys, type DietOrderAddModResult } from './residentsApi';
import type { AimDietFlucsInput } from './aimDietApi';

// Diet order lock (tblDiet_Flucs.IsLocked) and held EHR diet orders
// (tblDiet_Flucs_Lock). While a resident's current diet order is
// locked, the EHR processor holds each new EHR diet order here
// (Completed = 0) instead of replacing the diet, until someone
// applies or dismisses it.

// --- Types ---

/** One held EHR diet order (TblDiet_Flucs_Lock on the API). */
export interface HeldDietOrder {
  pKey_Diet_Flucs_Lock: number;
  /** The diet order it was held against. */
  fKey_Diet_Flucs: number;
  fKey_Patient: number;
  /** When the EHR sent it. */
  date_Downloaded: string;
  completed: boolean;
  fKey_Diet_Type1: number | null;
  fKey_Diet_Type2: number | null;
  fKey_Diet_Type3: number | null;
  fKey_Diet_Type4: number | null;
  fKey_Diet_Type5: number | null;
  fKey_Consist1: number | null;
  fKey_Consist2: number | null;
  fKey_Consist3: number | null;
  fKey_Consist4: number | null;
  fKey_Consist5: number | null;
  /** "Diet, diet, consistency" or "(none)". */
  newDietOrderText: string;
}

/** A held order from the facility-wide list: adds the resident and the lock. */
export interface FacilityHeldDietOrder extends HeldDietOrder {
  /** "Last, First". */
  fullName: string;
  /** Whether the diet order it waits on is still locked. */
  isLocked: boolean;
}

/** One resident's held orders, as the Dashboard lists them. */
export interface HeldResidentSummary {
  patientId: number;
  fullName: string;
  /** Oldest first, the order the review dialog lists them in. */
  orders: FacilityHeldDietOrder[];
  /** The most recently received held order. */
  newest: FacilityHeldDietOrder;
}

function receivedMs(h: HeldDietOrder): number {
  const t = new Date(h.date_Downloaded).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Groups the facility's held orders by resident: one entry per
 * resident, orders oldest first, residents with the most recently
 * received held order first (name breaks ties).
 */
export function groupHeldByResident(rows: FacilityHeldDietOrder[]): HeldResidentSummary[] {
  const byPatient = new Map<number, FacilityHeldDietOrder[]>();
  for (const r of rows) {
    const list = byPatient.get(r.fKey_Patient);
    if (list) list.push(r);
    else byPatient.set(r.fKey_Patient, [r]);
  }
  const out: HeldResidentSummary[] = [];
  for (const [patientId, list] of byPatient) {
    const orders = [...list].sort(
      (a, b) => receivedMs(a) - receivedMs(b) || a.pKey_Diet_Flucs_Lock - b.pKey_Diet_Flucs_Lock,
    );
    out.push({ patientId, fullName: orders[0].fullName ?? '', orders, newest: orders[orders.length - 1] });
  }
  out.sort(
    (a, b) => receivedMs(b.newest) - receivedMs(a.newest) || a.fullName.localeCompare(b.fullName),
  );
  return out;
}

/**
 * What a lock action or a delete sends back. When the resident's
 * current diet changed (held orders applied, or the current row
 * deleted), aimBefore / aimAfter carry the old and new diet so the
 * caller runs AIM as it does after an Add; both null otherwise.
 */
export interface DietOrderActionResult extends DietOrderAddModResult {
  aimBefore?: AimDietFlucsInput | null;
  aimAfter?: AimDietFlucsInput | null;
  heldOrdersApplied?: number;
  isLocked?: boolean;
}

// --- Query keys ---

export const dietOrderLockKeys = {
  all: ['dietOrderLock'] as const,
  held: (facilityId: number, patientId: number) =>
    [...dietOrderLockKeys.all, 'held', facilityId, patientId] as const,
  heldFacility: (facilityId: number) =>
    [...dietOrderLockKeys.all, 'heldFacility', facilityId] as const,
};

// --- Queries ---

/**
 * The resident's held EHR diet orders, oldest first. Errors are left
 * on the query (isError) so the page can say it couldn't check, rather
 * than showing "nothing held".
 */
export function useHeldDietOrders(facilityId: number, patientId: number, enabled = true) {
  return useQuery({
    queryKey: dietOrderLockKeys.held(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<HeldDietOrder[]>(
        API.patientDietFluctuationHeld(facilityId, patientId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && patientId > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Held EHR diet orders for every current resident of the facility
 * (Dashboard). Errors stay on the query (isError) so the card can say
 * it couldn't check instead of looking empty.
 */
export function useFacilityHeldDietOrders(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: dietOrderLockKeys.heldFacility(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<FacilityHeldDietOrder[]>(
        API.patientDietFluctuationHeldFacility(facilityId),
      );
      return data ?? [];
    },
    enabled: enabled && facilityId > 0,
    staleTime: 60 * 1000,
  });
}

// --- Mutations ---

function invalidateDietOrder(queryClient: ReturnType<typeof useQueryClient>, facilityId: number, patientId: number) {
  queryClient.invalidateQueries({ queryKey: residentKeys.dietHistory(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
  queryClient.invalidateQueries({ queryKey: dietOrderLockKeys.held(facilityId, patientId) });
  queryClient.invalidateQueries({ queryKey: dietOrderLockKeys.heldFacility(facilityId) });
}

export interface SetDietOrderLockInput {
  pKey_Diet_Flucs: number;
  locked: boolean;
}

/** Lock or unlock a diet order. Unlocking applies any held orders waiting on it. */
export function useSetDietOrderLock(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pKey_Diet_Flucs, locked }: SetDietOrderLockInput): Promise<DietOrderActionResult> => {
      const { data } = await apiClient.post<DietOrderActionResult>(
        API.patientDietFluctuationLock(facilityId, patientId, pKey_Diet_Flucs),
        { locked },
      );
      return data;
    },
    onSettled: () => invalidateDietOrder(queryClient, facilityId, patientId),
  });
}

export interface ResolveHeldDietOrderInput {
  pKey_Diet_Flucs_Lock: number;
  /** true: use the new diet order (removes the lock); false: keep the current one. */
  useNew: boolean;
}

/** Apply or dismiss one held EHR diet order. */
export function useResolveHeldDietOrder(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pKey_Diet_Flucs_Lock, useNew }: ResolveHeldDietOrderInput): Promise<DietOrderActionResult> => {
      const { data } = await apiClient.post<DietOrderActionResult>(
        API.patientDietFluctuationHeldResolve(facilityId, patientId, pKey_Diet_Flucs_Lock),
        { useNew },
      );
      return data;
    },
    onSettled: () => invalidateDietOrder(queryClient, facilityId, patientId),
  });
}
